import { mergeDeep } from '@apollo/client/utilities';
import { Formik } from 'formik';
import { useCurrentAccountCountryQueryOptions } from 'hooks/common/useCurrentAccountCountries';
import { usePushWithQuery } from 'hooks/routing';
import { useIsUsCurrentTalent } from 'hooks/talents/useCurrentTalentRegion';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import {
  CompanyPoolingTypeEnum,
  GetCountiresListDocument,
} from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import FormikAutoSaving from '@libs/ui/components/form/formik/FormikAutoSave';
import {
  ConnectedGraphSelect,
  ConnectedSelect,
} from '@libs/ui/components/form/select';

import { FilterState } from './types';

interface CompaniesFilterProps {
  filter: FilterState;
}

const COMPANIY_TYPE_OPTIONS = [
  CompanyPoolingTypeEnum.Company,
  CompanyPoolingTypeEnum.Recruiter,
].map((option) => ({
  text: `${option.slice(0, 1)}${option.slice(1).toLowerCase()}`,
  value: option,
}));

export const useAutoSaveHandler = () => {
  const push = usePushWithQuery();
  return useCallback(
    (newValues: FilterState) => {
      push({
        query: {
          type: newValues.type,
          country: newValues.country,
          lookingFor: newValues.lookingFor,
        },
      });
    },
    [push],
  );
};

const DEFAULT_FILTER: FilterState = {
  type: '' as unknown as any,
  country: '',
  lookingFor: '',
};

export const CompaniesFilter = ({ filter }: CompaniesFilterProps) => {
  const { t } = useTranslation('talent');
  const autoSaveHandler = useAutoSaveHandler();
  const initialValues = mergeDeep(DEFAULT_FILTER, filter);
  const isUsCurrentTalent = useIsUsCurrentTalent();
  const countriesQueryOptions = useCurrentAccountCountryQueryOptions();
  const companyTypesOptions = useMemo(() => {
    if (isUsCurrentTalent) {
      return COMPANIY_TYPE_OPTIONS.filter((i) => i.value !== 'RECRUITER');
    }
    return COMPANIY_TYPE_OPTIONS;
  }, [isUsCurrentTalent]);

  return (
    <Formik<FilterState>
      onSubmit={autoSaveHandler}
      initialValues={initialValues}
    >
      {({ setFieldValue }) => {
        return (
          <>
            <FormikAutoSaving debounceMs={1000} />
            <Grid container spacing={4}>
              <Grid item>
                <ConnectedSelect
                  name={modelPath<FilterState>((m) => m.type)}
                  options={companyTypesOptions}
                  fullWidth
                  variant="filled"
                  label={t('companies.filter.type')}
                  hideNoneValue
                />
              </Grid>
              <Grid item>
                <ConnectedGraphSelect
                  query={GetCountiresListDocument}
                  dataPath="getCountriesList"
                  dataMap={{ text: 'name', value: 'name' }}
                  fullWidth
                  queryOptions={{
                    variables: countriesQueryOptions,
                    onCompleted: (data) => {
                      if (isUsCurrentTalent) {
                        const usCountry = data.getCountriesList.find(
                          (i: any) => i.name === 'USA',
                        );
                        if (usCountry) {
                          setFieldValue('country', usCountry.name);
                        }
                      }
                    },
                  }}
                  label={t('companies.filter.country')}
                  name={modelPath<FilterState>((m) => m.country)}
                  variant="filled"
                />
              </Grid>
              {/* <Grid item> */}
              {/*   <ConnectedSelect */}
              {/*     name={modelPath<FilterState>((m) => m.lookingFor)} */}
              {/*     hideNoneValue */}
              {/*     options={[]} */}
              {/*     fullWidth */}
              {/*     disabled */}
              {/*     variant="filled" */}
              {/*     label="Looking for" */}
              {/*   /> */}
              {/* </Grid> */}
            </Grid>
          </>
        );
      }}
    </Formik>
  );
};
